/**
 * Bridge to Rebuy Smart Cart.
 *
 * Smart Cart opens itself only when it intercepts a product-form submit — that's its
 * "Product Form Submission Behavior: Stay on current page" setting, and it's why the
 * product page works. The subscription builder can't use that path: it resolves curated
 * coffees from an API, clears any existing `_rc_bundle` lines, then adds several lines at
 * once with per-line properties and selling plans. That's a fetch to /cart/add.js, so no
 * submit event fires and Smart Cart never hears about it. So we open it ourselves.
 *
 * Method names below come from the live Smart Cart V2 instance, not from guesswork.
 */

import {
  isSubscriptionLine,
  subscriptionHeadroom,
  subscriptionPlanBags,
} from './subscriptionCartGuard.js';

// rebuy.js is loaded async and Smart Cart mounts a Vue app after that, so on the first
// add of a session it may not be ready at the moment the customer clicks.
const READY_TIMEOUT_MS = 5000;
const POLL_INTERVAL_MS = 100;

let lastKnownCart = null;

function smartCart() {
  const rebuy = typeof window !== 'undefined' ? window.Rebuy : null;
  if (!rebuy) return null;
  return rebuy.SmartCart || rebuy.smartCart || null;
}

function isReady(cart) {
  return !!(cart && typeof cart.show === 'function');
}

export function isRebuyLoaded() {
  return !!(typeof window !== 'undefined' && window.Rebuy);
}

export function isRebuyCartAvailable() {
  return isReady(smartCart());
}

function waitForSmartCart(timeoutMs) {
  return new Promise(resolve => {
    let cart = smartCart();
    if (isReady(cart)) {
      resolve(cart);
      return;
    }

    const deadline = Date.now() + timeoutMs;
    const timer = setInterval(() => {
      cart = smartCart();
      if (isReady(cart)) {
        clearInterval(timer);
        resolve(cart);
      } else if (Date.now() > deadline) {
        clearInterval(timer);
        resolve(null);
      }
    }, POLL_INTERVAL_MS);
  });
}

/**
 * Re-renders Smart Cart from the given cart, so its lines aren't stale.
 *
 * setCartAsync reads `cart.items`, so the cart MUST be passed in — calling it bare throws
 * "Cannot read properties of undefined (reading 'items')". Pass the cart you already have
 * (e.g. the response from /cart/update.js) and this avoids a redundant fetch.
 */
export async function refreshRebuyCart(cart) {
  const smart = smartCart();
  if (!smart) return;

  let data = cart;
  if (!data) {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (res.ok) data = await res.json();
    } catch (_) {
      /* fall through to the event-based refresh below */
    }
  }

  try {
    if (data && typeof smart.setCartAsync === 'function') {
      await smart.setCartAsync(data);
    } else if (typeof smart.handleCartChangeEvent === 'function') {
      smart.handleCartChangeEvent();
    }
  } catch (err) {
    console.warn('[Coava] Rebuy Smart Cart refresh failed:', err);
  }
}

/**
 * Waits for Smart Cart to be ready, re-reads the cart we just changed, then opens it.
 * Resolves true only if the drawer was actually shown.
 */
export async function openRebuyCart() {
  const cart = await waitForSmartCart(READY_TIMEOUT_MS);

  if (!cart) {
    console.warn(
      `[Coava] Rebuy Smart Cart never became ready within ${READY_TIMEOUT_MS}ms.`,
      { rebuyLoaded: isRebuyLoaded(), smartCartFound: !!smartCart() },
    );
    return false;
  }

  // Smart Cart caches its cart; without this it opens showing stale lines. A refresh
  // failure must not stop the drawer from opening.
  await refreshRebuyCart();

  try {
    cart.show();
    return true;
  } catch (err) {
    console.error('[Coava] Rebuy Smart Cart show() threw:', err);
    return false;
  }
}

const WATCH_INTERVAL_MS = 800;

function cartSignature(cart) {
  return ((cart && cart.items) || []).map(l => `${l.key}:${l.quantity}`).join('|');
}

/**
 * Stops Rebuy's `+` from being clickable once the subscription is at its bag count, rather
 * than letting the quantity jump and then having the guard revert it.
 *
 * Smart Cart's template calls `canIncreaseQuantity(item)` to decide whether `+` is enabled,
 * and resolves it off the instance — verified by overriding it and watching all three
 * buttons go disabled. Note its body calls `getEffectiveQuantityLimits` as a closure
 * variable, so patching *that* would do nothing; `canIncreaseQuantity` is the only usable
 * seam. Add-ons and ordinary products fall straight through to Rebuy's own logic.
 *
 * Re-applied on every tick because Rebuy's `reInit` would otherwise silently drop it.
 */
const NOTICE_ID = 'coava-cart-notice';

/**
 * Tells the customer why checkout is blocked, since Rebuy exposes no hook for our own
 * validation messages. Injected into the drawer footer, above the checkout button.
 * Styles are inline because our component CSS is scoped and can't reach Rebuy's DOM.
 */
function renderSubscriptionNotice(cart) {
  const footer = document.querySelector('.rebuy-cart__flyout-footer');
  if (!footer) return;

  const short = subscriptionHeadroom(cart);
  let notice = document.getElementById(NOTICE_ID);

  if (short <= 0) {
    if (notice) notice.remove();
    return;
  }

  if (!notice) {
    notice = document.createElement('p');
    notice.id = NOTICE_ID;
    notice.setAttribute('role', 'alert');
    notice.style.cssText = 'margin:0 0 14px;padding:10px 12px;background:#fdeaec;'
      + 'color:#991522;font-size:13px;line-height:18px;text-align:center;';
    footer.insertBefore(notice, footer.firstChild);
  }

  const bags = subscriptionPlanBags(cart);
  const chosen = bags - short;
  notice.textContent = `Your subscription needs ${bags} bags — ${chosen} selected. `
    + `Add ${short} more to check out.`;
}

/**
 * Blocks checkout while the subscription is short of its bag count.
 *
 * Same seam as canIncreaseQuantity: Smart Cart's template resolves `checkout` off the
 * instance, so wrapping the property intercepts the button.
 */
function ensureCheckoutPatch(smart) {
  if (!smart || typeof smart.checkout !== 'function') return;
  if (smart.checkout.__coavaPatched) return;

  const original = smart.checkout;

  function patched(...args) {
    const cart = smart.cart && smart.cart.items ? smart.cart : lastKnownCart;
    if (subscriptionHeadroom(cart) > 0) {
      renderSubscriptionNotice(cart);
      return undefined;
    }
    return original.apply(this, args);
  }
  patched.__coavaPatched = true;

  smart.checkout = patched;
}

function ensureQuantityLimitPatch(smart) {
  if (!smart || typeof smart.canIncreaseQuantity !== 'function') return;
  if (smart.canIncreaseQuantity.__coavaPatched) return;

  const original = smart.canIncreaseQuantity;

  function patched(item) {
    // smart.cart is what's being rendered, so it's the right basis for the decision.
    const cart = smart.cart && smart.cart.items ? smart.cart : lastKnownCart;
    if (isSubscriptionLine(item) && subscriptionHeadroom(cart) <= 0) return false;
    return original.call(this, item);
  }
  patched.__coavaPatched = true;

  smart.canIncreaseQuantity = patched;
}

/**
 * Calls onChange(cart) whenever the customer edits the cart from inside Smart Cart.
 *
 * Rebuy mutates the Shopify cart directly and doesn't notify the page, so we poll
 * /cart.js — but only while the drawer is actually on screen, plus one final read once
 * it closes. That keeps it cheap and covers the drawer being opened from the header
 * cart icon as well as from the subscription builder.
 *
 * For instant updates instead of polling, enable Rebuy → Smart Cart → Global Settings →
 * Event Callbacks (LineItemRemoved / LineItemIncrease / LineItemDecrease) and have each
 * call window.coavaSyncBuilderFromCart().
 *
 * Returns a stop function.
 */
export function watchRebuyCart(onChange) {
  let signature = null;
  let wasVisible = false;
  let checking = false;

  async function check() {
    if (checking) return;
    checking = true;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      lastKnownCart = cart;
      const next = cartSignature(cart);
      if (signature === null) {
        signature = next; // first read after opening is the baseline, not a change
      } else if (next !== signature) {
        signature = next;
        onChange(cart);
      }
    } catch (_) {
      /* offline or navigating away — nothing to do */
    } finally {
      checking = false;
    }
  }

  const timer = setInterval(() => {
    const smart = smartCart();
    // Both install once Smart Cart mounts, and survive Rebuy's reInit dropping them.
    ensureQuantityLimitPatch(smart);
    ensureCheckoutPatch(smart);

    const visible = !!(smart && smart.visible);
    // Keep the notice current so the customer sees the shortfall before clicking checkout.
    if (visible) renderSubscriptionNotice((smart.cart && smart.cart.items) ? smart.cart : lastKnownCart);
    if (visible || wasVisible) check();
    wasVisible = visible;
  }, WATCH_INTERVAL_MS);

  return () => clearInterval(timer);
}
